/**
 * Комментарий-выноска на чертеже: рамка с текстом и стрелка на точку плана.
 *
 * Рамка белая, обводка, текст и стрелка — цветом комментария. Стрелка выходит из края
 * рамки и кончается наконечником в точке, на которую указывает комментарий; стоит рамка
 * на самой точке — стрелки нет. У выделенного рамка синяя, на конце стрелки ручка (её
 * тянут), а в правом нижнем углу рамки кнопка ▾ — меню: шрифт, цвет, удалить. Кнопка видна
 * и пока пишут текст: нажал — текст сохранился и сразу открылось меню. Пока текст правят в окошке
 * поверх холста, свой текст выноска не рисует. Зоны захвата прозрачные, ручка и кнопка
 * помечены «не для экспорта» — на лист PDF попадает только сама выноска.
 */
import type { MouseEvent, PointerEvent } from "react";
import type { PlanComment } from "@/shared/plan";
import { COMMENT_LINE_HEIGHT, commentBox, pointerStart } from "../logic";

const SELECTED = "#2f6feb";

/** За что взяли комментарий: за рамку или за конец стрелки. */
export type CommentPart = "box" | "anchor";

interface Props {
	comment: PlanComment;
	selected: boolean;
	/** Текст сейчас правят в окошке — свой не рисуем */
	editing: boolean;
	mmPerPx: number;
	interactive: boolean;
	onPointerDown?: (e: PointerEvent<SVGElement>, part: CommentPart) => void;
	onDoubleClick?: (e: MouseEvent<SVGElement>) => void;
	/** Правый клик или кнопка ▾ — открыть меню комментария */
	onMenu?: (e: MouseEvent<SVGElement> | PointerEvent<SVGElement>) => void;
}

export const CommentShape = ({
	comment,
	selected,
	editing,
	mmPerPx,
	interactive,
	onPointerDown,
	onDoubleClick,
	onMenu,
}: Props) => {
	const px = (n: number) => n * mmPerPx;
	const box = commentBox(comment, mmPerPx);
	const start = pointerStart(box, comment.anchor);
	const color = comment.color;
	// Наконечник стрелки: треугольник вдоль линии, остриём в точку
	const arrow = (() => {
		if (!start) return null;
		const dx = comment.anchor.x - start.x;
		const dy = comment.anchor.y - start.y;
		const length = Math.hypot(dx, dy);
		if (length < px(4)) return null;
		const ux = dx / length;
		const uy = dy / length;
		const size = px(7);
		const half = px(3);
		const baseX = comment.anchor.x - ux * size;
		const baseY = comment.anchor.y - uy * size;
		return `${comment.anchor.x},${comment.anchor.y} ${baseX - uy * half},${baseY + ux * half} ${baseX + uy * half},${baseY - ux * half}`;
	})();
	const textX = box.x + box.padding;
	const firstBaseline = box.y + box.padding * 0.7 + box.fontSize;
	// Чуть наружу от угла: окошко ввода лежит поверх рамки и не должно закрывать кнопку
	const menuX = box.x + box.width + px(4);
	const menuY = box.y + box.height + px(4);

	return (
		<g style={{ pointerEvents: interactive ? "auto" : "none" }}>
			{start && (
				<line
					x1={start.x}
					y1={start.y}
					x2={comment.anchor.x}
					y2={comment.anchor.y}
					stroke={color}
					strokeWidth={px(1.1)}
				/>
			)}
			{arrow && <polygon points={arrow} fill={color} />}
			<rect
				x={box.x}
				y={box.y}
				width={box.width}
				height={box.height}
				rx={px(3)}
				fill="#ffffff"
				stroke={selected ? SELECTED : color}
				strokeWidth={px(selected ? 1.5 : 1)}
			/>
			{!editing && (
				<text
					x={textX}
					y={firstBaseline}
					fontSize={box.fontSize}
					fill={color}
					style={{ userSelect: "none" }}
				>
					{box.lines.map((line, index) => (
						<tspan
							// Строки комментария не переставляются — индекс как ключ здесь честный
							key={index}
							x={textX}
							dy={index === 0 ? 0 : box.fontSize * COMMENT_LINE_HEIGHT}
						>
							{line || " "}
						</tspan>
					))}
				</text>
			)}

			{/* Зона захвата рамки: тянуть, двойной клик — править текст, правый клик — меню */}
			<rect
				data-role="comment"
				data-id={comment.id}
				x={box.x}
				y={box.y}
				width={box.width}
				height={box.height}
				fill="transparent"
				style={{ cursor: interactive ? "move" : "default" }}
				onPointerDown={(e) => onPointerDown?.(e, "box")}
				onDoubleClick={onDoubleClick}
				onContextMenu={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onMenu?.(e);
				}}
			/>
			{/* Ручка конца стрелки — только у выделенного: тянешь — стрелка указывает в новое место */}
			{selected && start && (
				<circle
					data-plan-export-exclude
					data-role="comment-anchor"
					data-id={comment.id}
					cx={comment.anchor.x}
					cy={comment.anchor.y}
					r={px(5)}
					fill="#ffffff"
					stroke={SELECTED}
					strokeWidth={px(1.5)}
					style={{ cursor: "crosshair" }}
					onPointerDown={(e) => onPointerDown?.(e, "anchor")}
				/>
			)}
			{/* Кнопка меню ▾ в правом нижнем углу рамки выделенного комментария */}
			{selected && (
				<g
					data-plan-export-exclude
					data-role="comment-menu"
					style={{ cursor: "pointer" }}
					onPointerDown={(e) => {
						e.stopPropagation();
						onMenu?.(e);
					}}
				>
					<circle cx={menuX} cy={menuY} r={px(8)} fill={SELECTED} />
					<path
						d={`M${menuX - px(3)} ${menuY - px(1.2)}L${menuX} ${menuY + px(1.8)}L${menuX + px(3)} ${menuY - px(1.2)}`}
						fill="none"
						stroke="#ffffff"
						strokeWidth={px(1.6)}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</g>
			)}
		</g>
	);
};
